import { Flex, Box } from '@components/grid';
import { P } from '@components/typography/new';
import { useRootStore } from '@nandosaus/state-management';
import { orderItemPropTypes } from '@prop-types/order-item';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { useFeature } from '@hooks/use-feature';
import { DaypartWarning } from '@components/daypart-warning';

import styled from 'styled-components';

import { TextPill } from '@components/text-pill';
import { QuantityPicker } from '@components/quantity-picker';
import { Icon } from '@components/icons';
import { analytics } from '../../analytics';

const FlexWithDivider = styled(Flex)`
  :not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.warmWhite3};
  }

  cursor: ${({ disableOnClick }) => (disableOnClick ? 'default' : 'pointer')};
`;

const Pricing = ({ formattedPrice }) => {
  return (
    <>
      <Flex alignItems="stretch" flexShrink={0} justifyContent="right">
        <P variant="small">{formattedPrice}</P>
      </Flex>
    </>
  );
};

Pricing.propTypes = {
  formattedPrice: PropTypes.string.isRequired,
};

const QuantityIndicator = ({ quantity }) => {
  return (
    <Flex
      alignItems="center"
      borderColor="grey40"
      borderStyle="solid"
      borderWidth="1px"
      height="32px"
      justifyContent="center"
      mr="16px"
      minWidth="32px"
      borderRadius="4.5px"
    >
      <P variant="small-semi-bold">{quantity}</P>
    </Flex>
  );
};

QuantityIndicator.propTypes = { quantity: PropTypes.number.isRequired };

const UnderlineOnHoverLabel = styled(P)`
  text-decoration: ${({ hovering, disableOnClick }) => (hovering && !disableOnClick ? 'underline' : 'none')};
`;

const OrderItemListItem = ({
  orderItem,
  setProductDetailModalOpen,
  disableOnClick,
  showDaypart = true,
  tags = [],
  onRemove = null,
  removeOnly = false,
}) => {
  const { ProductDetailsState, CartStore, MenuStore, OrderingContextStore } = useRootStore();
  const { quantity, product, formattedKilojoules, totals, isUpsellItem } = orderItem;
  const cartRemovalEnabled = useFeature('remove-from-cart');

  const [hovering, setHovering] = useState(false);

  // `removeOnly` rows (e.g. the S&D free gift) are static: no edit-on-click and a
  // trash button in place of the quantity picker.
  const isStatic = disableOnClick || removeOnly;

  // Find the category for this product to check for daypart
  const productCategory = product?.id ? MenuStore.getProductCategory(product.id) : null;
  const daypart = productCategory?.daypart;
  const timezone = OrderingContextStore.restaurant?.timezone;

  const editOrderItem = useCallback(() => {
    analytics.track(ANALYTICS_EVENTS.PRODUCT_UPDATE_CLICKED, { product: orderItem.product.name });
    ProductDetailsState.editOrderItem({ orderItem });
    setProductDetailModalOpen(true);
  }, [ProductDetailsState, orderItem, setProductDetailModalOpen]);

  // Handler functions for QuantityPicker
  const handleQuantityChange = useCallback(
    newQuantity => {
      // Update the order item quantity in the cart
      if (newQuantity > 0) {
        // Use spliceOrderItemById to update the existing order item
        const updatedOrderItem = { ...orderItem, quantity: newQuantity };
        CartStore.spliceOrderItemById(orderItem.id, updatedOrderItem);
      }
    },
    [CartStore, orderItem]
  );

  const handleIncrement = useCallback(() => {
    handleQuantityChange(quantity + 1);
  }, [handleQuantityChange, quantity]);

  const handleDecrement = useCallback(() => {
    const newQuantity = quantity - 1;
    handleQuantityChange(newQuantity);
  }, [handleQuantityChange, quantity]);

  // When there's no resolved image (e.g. the S&D Free Delivery sentinel, where
  // `resolvedImage` is null), render a placeholder instead of a bare <img> with
  // no src (which shows a broken-image icon).
  const renderThumbnail = () => {
    if (orderItem.product.resolvedImage) {
      return (
        <Image
          src={orderItem.product.resolvedImage}
          alt={product?.longName || orderItem?.name || product?.name || 'menu item'}
        />
      );
    }

    return (
      <ImagePlaceholder alignItems="center" justifyContent="center">
        <PlaceholderGlyph src="/static/images/chili.png" alt="" />
      </ImagePlaceholder>
    );
  };

  return (
    <FlexWithDivider py="20px" key={orderItem.id} disableOnClick={disableOnClick}>
      {disableOnClick ? <QuantityIndicator quantity={quantity} /> : renderThumbnail()}

      <Flex
        flexDirection="column"
        flex="1 1 auto"
        onClick={isStatic ? undefined : editOrderItem}
        onMouseOver={() => setHovering(true)}
        onMouseOut={() => setHovering(false)}
      >
        <Flex justifyContent="space-between" width="100%" gap="16px">
          <UnderlineOnHoverLabel variant="small-semi-bold" hovering={hovering} disableOnClick={isStatic}>
            {product?.longName || orderItem?.name || product?.name}
          </UnderlineOnHoverLabel>

          <Pricing formattedPrice={totals?.formattedPrice || orderItem?.price} />
        </Flex>
        <Flex justifyContent="space-between" gap="16px" pt="8px" width="100%">
          <Flex flexDirection="column">
            {product?.choices &&
              product.choices.map(choice => {
                // Handle both Map (from cart store) and Array (from reorder modal) structures
                let values = [];

                if (orderItem?.choices) {
                  if (typeof orderItem.choices.get === 'function') {
                    // Cart store case: choices is an MSTMap (has .get method)
                    values = orderItem.choices.get(choice.name) || [];
                  } else if (Array.isArray(orderItem.choices)) {
                    // Reorder modal case: choices is an array
                    values = orderItem.choices.filter(currentChoice => currentChoice.choiceType === choice.name);
                  }
                }

                return values.map(value => (
                  <P variant="extra-small" color="grey50" key={value.option?.name || value.name}>
                    {value.option?.name || value.name}
                  </P>
                ));
              })}
            {formattedKilojoules && (
              <P variant="extra-small" mt={product?.choices?.length ? '4px' : 0} color="grey50">
                {formattedKilojoules}
              </P>
            )}

            {isUpsellItem && (
              <Box mt="12px">
                <TextPill
                  text="Added"
                  borderRadius="4px"
                  color="white"
                  backgroundColor="black"
                  height="fit-content"
                  width="fit-content"
                  py="4px"
                  px="7px"
                />
              </Box>
            )}

            {tags.length > 0 && (
              <Flex mt="12px" gap="8px" flexWrap="wrap">
                {tags.map(tag => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </Flex>
            )}
          </Flex>

          {removeOnly ? (
            <Flex alignItems="flex-end">
              <RemoveButton
                type="button"
                onClick={onRemove ?? undefined}
                aria-label={`Remove ${product?.longName || orderItem?.name || 'item'}`}
              >
                <Icon name="trash" width="20px" height="20px" />
              </RemoveButton>
            </Flex>
          ) : (
            !disableOnClick && (
              <Flex alignItems="flex-end" width="100px">
                <QuantityPicker
                  quantity={quantity}
                  increment={handleIncrement}
                  decrement={handleDecrement}
                  onQuantityChange={handleQuantityChange}
                  onRemove={cartRemovalEnabled ? () => CartStore.removeOrderItem(orderItem) : undefined}
                  iconSize="18px"
                  buttonSize="33.33px"
                  height="33.33px"
                  min={0}
                  max={99}
                  showMinusOnMin
                  isCateringMode={MenuStore?.isCateringMenu}
                  minQty={product?.minQty || 1}
                  qtyIncrement={product?.qtyIncrement || 1}
                />
              </Flex>
            )
          )}
        </Flex>
        {MenuStore?.isCateringMenu && (product?.minQty > 1 || product?.qtyIncrement > 1) && (
          <P textAlign="end" variant="extra-small" mt="8px">
            {product?.minQty && `Minimum qty ${product?.minQty}`}
            {product?.minQty && product?.qtyIncrement && '. '}
            {product?.qtyIncrement && `Order in multiples of ${product?.qtyIncrement}.`}
          </P>
        )}
        {!MenuStore?.isCateringMenu && showDaypart && (
          <DaypartWarning mt="8px" daypart={daypart} timezone={timezone} categoryName={productCategory?.name} />
        )}
      </Flex>
    </FlexWithDivider>
  );
};

const Image = styled.img`
  width: 80px;
  height: 80px;
  min-width: 80px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 16px;
  overflow: hidden;
`;

// Fallback when an order item has no image (e.g. the S&D Free Delivery sentinel,
// where `resolvedImage` is null). Mirrors the gift-picker placeholder
// (surprise-and-delight/gift-option): a grey box with the chilli glyph centred.
// Sized to match the `Image` thumbnail above so the row layout is unchanged.
const ImagePlaceholder = styled(Flex)`
  width: 80px;
  height: 80px;
  min-width: 80px;
  background: ${({ theme }) => theme.colors.grey100};
  border-radius: 8px;
  margin-right: 16px;
  overflow: hidden;
`;

const PlaceholderGlyph = styled.img`
  height: 40px;
  width: auto;
`;

// Generic row badge (e.g. "Free gift"), matching the Figma free-gift badge:
// Barlow 14px/600, black pill with 4px radius.
const Tag = styled.span`
  align-self: flex-start;
  background: ${({ theme }) => theme.colors.black};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.white};
  display: inline-flex;
  font-family: 'Barlow', Calibri, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  padding: 4px 8px;
`;

const RemoveButton = styled.button`
  appearance: none;
  align-items: center;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  margin: 0;
  padding: 4px;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    opacity: 0.7;
  }
`;

OrderItemListItem.propTypes = {
  orderItem: orderItemPropTypes.isRequired,
  setProductDetailModalOpen: PropTypes.func.isRequired,
  disableOnClick: PropTypes.bool,
  showDaypart: PropTypes.bool,
  // Generic badges rendered under the item meta (e.g. ['Free gift']).
  tags: PropTypes.arrayOf(PropTypes.string),
  // When true, render a trash button (calling onRemove) instead of the quantity
  // picker, and make the row static (no edit-on-click).
  removeOnly: PropTypes.bool,
  onRemove: PropTypes.func,
};

OrderItemListItem.defaultProps = {
  disableOnClick: false,
  showDaypart: true,
  tags: [],
  removeOnly: false,
  onRemove: null,
};

const ObservedOrderItemListItem = observer(OrderItemListItem);

export { ObservedOrderItemListItem as OrderItemListItem };
